import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUsers, createUser, deleteUser, clearUserMessage, selectUsersLoading, selectUsersError, selectUsersMessage } from '../features/users/userSlice';
import { selectIsAdmin } from '../features/auth/authSlice';
import UserList from '../components/users/UserList';
import UserForm from '../components/users/UserForm';
import EditUser from '../components/users/EditUser';
import { useToast } from '../contexts/ToastContext';
import BackButton from '../components/ui/BackButton';

const UserManagement = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectUsersLoading);
  const isError = useSelector(selectUsersError);
  const message = useSelector(selectUsersMessage);
  const isAdmin = useSelector(selectIsAdmin);
  const toast = useToast();

  const [view, setView] = useState('list'); 
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      dispatch(getAllUsers());
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (isError && message) {
      toast.showError(message);
      dispatch(clearUserMessage());
    }
  }, [isError, message, toast, dispatch]);

  const handleCreateNew = () => {
    setSelectedUser(null);
    setView('create');
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setView('edit');
  };

  const handleDelete = (user) => {
    setDeleteConfirm(user);
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      dispatch(deleteUser(deleteConfirm.id))
        .unwrap()
        .then(() => {
          toast.showSuccess('User deleted successfully');
          setDeleteConfirm(null);
          // Refresh users list after deletion
          dispatch(getAllUsers());
        })
        .catch((error) => {
          toast.showError(`Failed to delete user: ${error}`);
        });
    }
  };

  const handleCreateSubmit = (userData) => {
    dispatch(createUser(userData))
      .unwrap()
      .then(() => {
        toast.showSuccess('User created successfully');
        setView('list');
        // Refresh users list after creation
        dispatch(getAllUsers());
      })
      .catch((error) => {
        toast.showError(`Failed to create user: ${error}`);
      });
  };

  const handleEditSuccess = () => {
    setView('list');
    setSelectedUser(null);
    // Refresh users list after edit
    dispatch(getAllUsers());
  };

  const handleCancel = () => {
    setView('list');
    setSelectedUser(null);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-silver-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-crystal-white border border-steel/20 shadow-minimal">
            <div className="px-8 py-12 text-center">
              <div className="mx-auto w-16 h-16 mb-6 flex items-center justify-center bg-error/10 border-2 border-error/20">
                <svg className="w-8 h-8 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-display font-bold text-deep-jungle mb-3">Access Denied</h3>
              <p className="text-steel max-w-md mx-auto leading-relaxed">
                You do not have the required administrator privileges to access user management functionality.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-silver-mist">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back Button for list view */}
        {view === 'list' && (
          <div className="mb-6">
            <BackButton />
          </div>
        )}

        {/* Navigation Breadcrumb */}
        {view !== 'list' && (
          <div className="mb-6">
            <nav className="flex items-center space-x-2 text-sm text-steel">
              <button
                onClick={() => setView('list')}
                className="hover:text-fresh-leaf transition-colors duration-200 focus:outline-none focus:text-fresh-leaf"
              >
                User Management
              </button>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-deep-jungle font-medium">
                {view === 'create' ? 'Create User' : 'Edit User'}
              </span>
            </nav>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-crystal-white border border-steel/20 shadow-minimal">
          {view === 'list' && (
            <UserList
              onCreateNew={handleCreateNew}
              onEdit={handleEdit}
              onDelete={handleDelete}
              initialLoad={true}
            />
          )}

          {view === 'create' && (
            <div className="p-8">
              <div className="mb-8 pb-6 border-b border-steel/20">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-display font-bold text-deep-jungle">Create New User</h2>
                    <p className="text-steel mt-1">Add a new user to the system with appropriate permissions</p>
                  </div>
                  <button
                    onClick={handleCancel}
                    className="inline-flex items-center px-4 py-2 border border-steel/30 text-sm font-medium text-steel bg-crystal-white hover:bg-silver-mist hover:border-steel/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-fresh-leaf/25"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to List
                  </button>
                </div>
              </div>
              <UserForm
                onSubmit={handleCreateSubmit}
                onCancel={handleCancel}
                isLoading={isLoading}
              />
            </div>
          )}

          {view === 'edit' && selectedUser && (
            <div className="p-8">
              <div className="mb-8 pb-6 border-b border-steel/20">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-display font-bold text-deep-jungle">Edit User</h2>
                    <p className="text-steel mt-1">Update user information and permissions</p>
                  </div>
                  <button
                    onClick={handleCancel}
                    className="inline-flex items-center px-4 py-2 border border-steel/30 text-sm font-medium text-steel bg-crystal-white hover:bg-silver-mist hover:border-steel/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-fresh-leaf/25"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to List
                  </button>
                </div>
              </div>
              <EditUser
                user={selectedUser}
                onCancel={handleCancel}
                onSuccess={handleEditSuccess}
              />
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-carbon-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-md mx-auto">
              <div className="bg-crystal-white border border-steel/20 shadow-depth">
                <div className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-error/10 border-2 border-error/20 flex items-center justify-center">
                      <svg className="w-6 h-6 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-display font-bold text-deep-jungle">Confirm Deletion</h3>
                      <p className="text-steel text-sm mt-1">This action cannot be undone</p>
                    </div>
                  </div>
                  
                  <div className="mb-8">
                    <p className="text-graphite leading-relaxed">
                      Are you sure you want to delete user{' '}
                      <span className="font-medium text-deep-jungle">"{deleteConfirm.username}"</span>? This will permanently remove their account and all associated data.
                    </p>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row gap-3">
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="flex-1 px-4 py-3 border border-steel/30 text-sm font-medium text-steel bg-crystal-white hover:bg-silver-mist hover:border-steel/50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-fresh-leaf/25"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDelete}
                      disabled={isLoading}
                      className="flex-1 px-4 py-3 border border-transparent text-sm font-medium text-crystal-white bg-error hover:bg-error/90 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-error/25"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-crystal-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Deleting...
                        </span>
                      ) : (
                        'Delete User'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
